import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import * as members from '../../api/members';
import type { AppMember, AppRole } from '@/backend/types';
import { Button, Chip, EmptyState, Screen, SectionLabel, TextField } from '../../ui';

// The two the phone offers. 'owner' cannot be invited and 'blocked' is not a grant.
const ROLES: AppRole[] = ['member', 'editor'];

/** Ported from src/app/(main)/members/[appId].tsx. */
export default function Members() {
  const { appId = '' } = useParams();
  const [list, setList] = useState<AppMember[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [lastInvite, setLastInvite] = useState<{ token: string } | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AppRole>('member');
  const [busy, setBusy] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<string | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setList(await members.fetchMembers(appId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the members of this app.');
    }
  }, [appId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function invite(): Promise<void> {
    if (!email.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const invite = await members.inviteMember(appId, { email: email.trim(), role });
      setLastInvite(invite);
      setEmail('');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That invitation could not be sent.');
    } finally {
      setBusy(false);
    }
  }

  async function confirmRemove(): Promise<void> {
    const userId = pendingRemoval;
    dialog.current?.close();
    setPendingRemoval(null);
    if (!userId) return;
    try {
      await members.removeMember(appId, userId);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That member could not be removed.');
    }
  }

  return (
    <Screen title="Members">
      {error && <p role="alert" className="type-caption field-error-text">{error}</p>}

      <SectionLabel>Invite someone</SectionLabel>
      <TextField label="Email" type="email" value={email} onChangeText={setEmail} />
      <label className="type-caption field-label" htmlFor="role">Role</label>
      <select id="role" className="field" value={role} onChange={(e) => setRole(e.target.value as AppRole)}>
        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      <Button title="Send invite" onPress={() => void invite()} loading={busy} disabled={!email.trim()} />

      {lastInvite?.token && (
        <p role="status" className="type-body publish-url">
          Invite link: <code>{`${window.location.origin}/invite/${lastInvite.token}`}</code>
        </p>
      )}

      <SectionLabel>Who has access</SectionLabel>
      {list.length === 0 ? (
        <EmptyState icon="account" title="No members yet" body="Invite someone above to share this app." />
      ) : (
        <ul className="project-list">
          {list.map((m) => (
            <li key={m.userId} className="card clay-card project-row">
              <span className="project-text">
                <span className="type-heading">{m.email ?? m.userId}</span>
                <span className="type-caption project-meta">Joined {m.joinedAt?.slice(0, 10)}</span>
              </span>
              <Chip label={m.role} tone={m.role === 'owner' ? 'primary' : 'neutral'} />
              {m.role !== 'owner' && (
                <Button
                  title="Remove"
                  variant="ghost"
                  onPress={() => {
                    setPendingRemoval(m.userId);
                    dialog.current?.showModal();
                  }}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      <dialog ref={dialog} aria-label="Remove this member" className="confirm-dialog">
        <h2 className="type-title">Remove this member?</h2>
        <p className="type-body">They lose access to the app immediately.</p>
        <div className="build-actions">
          <Button title="Yes, remove" variant="dangerSoft" onPress={() => void confirmRemove()} />
          <Button title="Cancel" variant="ghost" onPress={() => { dialog.current?.close(); setPendingRemoval(null); }} />
        </div>
      </dialog>
    </Screen>
  );
}
